import React, { createContext, forwardRef, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Snackbar,
  Typography,
  useMediaQuery
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const DownloadSnackbarContext = createContext(null);

const AUTO_HIDE_MS = {
  complete: 6000,
  empty: 8000,
  error: 10000
};

const STATUS = {
  processing: {
    Icon: HourglassEmptyOutlinedIcon,
    title: "Preparing export",
    tone: "#4D7FA6",
    detail: ({ label = "Your export" }) => `${label} is being prepared.`
  },
  ready: {
    Icon: FileDownloadOutlinedIcon,
    title: "Export ready",
    tone: "#4DA685",
    detail: ({ label = "Your export", filename }) => (
      filename ? `${label} is ready: ${filename}` : `${label} is ready to download.`
    )
  },
  complete: {
    Icon: CheckCircleOutlineIcon,
    title: "Export downloaded",
    tone: "#4DA685",
    detail: ({ label = "Your export", filename }) => (
      filename ? `${filename} has been downloaded.` : `${label} has been downloaded.`
    )
  },
  empty: {
    Icon: InfoOutlinedIcon,
    title: "Nothing to export",
    tone: "#8A6E2F",
    detail: ({ label = "This export" }) => `${label} did not return any rows.`
  },
  error: {
    Icon: ErrorOutlineIcon,
    title: "Export failed",
    tone: "#C25342",
    detail: ({ label = "Your export", message }) => message || `${label} could not be completed.`
  }
};

const buildSnack = (type, options = {}) => ({
  id: options.id || `download-${Date.now()}`,
  label: options.label,
  filename: options.filename,
  downloadUrl: options.downloadUrl,
  expiresAt: options.expiresAt,
  message: options.message,
  actionLabel: options.actionLabel,
  type,
  autoHideDuration: type === "ready" || type === "processing" ? null : AUTO_HIDE_MS[type],
  createdAt: Date.now()
});

const getActionLabel = ({ actionLabel, filename }) => {
  if (actionLabel) return actionLabel;
  return filename?.toLowerCase?.().endsWith(".csv") ? "Download CSV" : "Open file";
};

const DownloadSnackbarContent = forwardRef(function DownloadSnackbarContent({ snack, onDismiss }, ref) {
  const [expired, setExpired] = useState(false);
  useEffect(() => {
    const expiry = Date.parse(snack.expiresAt);
    if (!Number.isFinite(expiry)) { setExpired(false); return; }
    setExpired(expiry <= Date.now());
    const timer = setTimeout(() => setExpired(true), Math.max(0, expiry - Date.now()));
    return () => clearTimeout(timer);
  }, [snack.expiresAt]);
  const status = STATUS[snack.type] || STATUS.processing;
  const { Icon } = status;
  const actionLabel = getActionLabel(snack);

  return (
    <Box
      ref={ref}
      data-testid={`download-snackbar-${snack.id}`}
      role={snack.type === "error" ? "alert" : "status"}
      sx={{
        display: "grid",
        gridTemplateColumns: "auto minmax(0, 1fr) auto",
        gap: 1.25,
        alignItems: "flex-start",
        width: { xs: "calc(100vw - 32px)", sm: 420 },
        maxWidth: "calc(100vw - 32px)",
        p: 1.5,
        borderRadius: "8px",
        border: "1px solid rgba(39, 41, 49, 0.1)",
        borderLeft: `4px solid ${status.tone}`,
        bgcolor: "#fff",
        boxShadow: "0 10px 30px rgba(39, 41, 49, 0.14)",
        color: "#272931",
        fontFamily: "Assistant, sans-serif"
      }}
    >
      <Icon sx={{ color: status.tone, mt: 0.25, fontSize: 22 }} />
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1.25 }}>
          {expired ? "Download link expired" : status.title}
        </Typography>
        <Typography sx={{ mt: 0.25, fontSize: 13, lineHeight: 1.35, color: "rgba(39, 41, 49, 0.72)" }}>
          {expired ? "Export again to create a new download link." : status.detail(snack)}
        </Typography>
        {snack.downloadUrl && !expired && (
          <Button
            component="a"
            href={snack.downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onDismiss}
            size="small"
            sx={{
              mt: 1,
              px: 1.25,
              py: 0.5,
              minHeight: 30,
              borderRadius: "6px",
              bgcolor: "#272931",
              color: "#fff",
              fontSize: 12,
              fontWeight: 700,
              textTransform: "none",
              "&:hover": { bgcolor: "#3a3d49" }
            }}
          >
            {actionLabel}
          </Button>
        )}
      </Box>
      <IconButton
        aria-label="Dismiss download notification"
        onClick={onDismiss}
        size="small"
        sx={{ color: "rgba(39, 41, 49, 0.55)", p: 0.5 }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
});

export function DownloadSnackbarProvider({ children }) {
  const [snackbars, setSnackbars] = useState([]);
  const isMobile = useMediaQuery("(max-width:600px)");

  const dismissDownloadSnackbar = useCallback((id) => {
    setSnackbars((current) => current.filter((snack) => snack.id !== id));
  }, []);

  const upsertSnackbar = useCallback((type, options) => {
    const snack = buildSnack(type, options);
    setSnackbars((current) => {
      const index = current.findIndex((item) => item.id === snack.id);
      if (index === -1) return [...current, snack].slice(-3);
      return current.map((item, itemIndex) => itemIndex === index ? { ...item, ...snack } : item);
    });
    return snack.id;
  }, []);

  const value = useMemo(() => ({
    dismissDownloadSnackbar,
    showDownloadProcessing: (options) => upsertSnackbar("processing", options),
    showDownloadReady: (options) => upsertSnackbar("ready", options),
    showDownloadComplete: (options) => upsertSnackbar("complete", options),
    showDownloadEmpty: (options) => upsertSnackbar("empty", options),
    showDownloadError: (options) => upsertSnackbar("error", options)
  }), [dismissDownloadSnackbar, upsertSnackbar]);

  return (
    <DownloadSnackbarContext.Provider value={value}>
      {children}
      {snackbars.map((snack, index) => (
        <Snackbar
          key={snack.id}
          open
          autoHideDuration={snack.autoHideDuration}
          anchorOrigin={{ vertical: "bottom", horizontal: isMobile ? "center" : "right" }}
          onClose={(_, reason) => {
            if (reason !== "clickaway") dismissDownloadSnackbar(snack.id);
          }}
          sx={{ bottom: { xs: 16 + index * 112, sm: 24 + index * 112 } }}
        >
          <DownloadSnackbarContent
            snack={snack}
            onDismiss={() => dismissDownloadSnackbar(snack.id)}
          />
        </Snackbar>
      ))}
    </DownloadSnackbarContext.Provider>
  );
}

export function useDownloadSnackbar() {
  const context = useContext(DownloadSnackbarContext);
  if (!context) {
    throw new Error("useDownloadSnackbar must be used within DownloadSnackbarProvider");
  }
  return context;
}
